import { router, useLocalSearchParams } from 'expo-router';
import { MessageSquareText, Pencil, RefreshCcw, ShieldCheck } from 'lucide-react-native';
import { type ReactNode, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { FadeIn } from '../../../../src/components/animations';
import { BackButton } from '../../../../src/components/primitives/BackButton';
import { Badge } from '../../../../src/components/primitives/Badge';
import { Button } from '../../../../src/components/primitives/Button';
import { HStack, VStack } from '../../../../src/components/primitives/Stack';
import { Tabs } from '../../../../src/components/primitives/Tabs';
import { Body, Caption, Heading2, Heading4 } from '../../../../src/components/primitives/Text';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useTranslation } from '../../../../src/contexts/TranslationContext';
import { AgentAnalyticsPanel } from '../../../../src/features/voice-agents/components/AgentAnalyticsPanel';
import { AgentNameField } from '../../../../src/features/voice-agents/components/AgentNameField';
import { ExperimentsPanel } from '../../../../src/features/voice-agents/components/ExperimentsPanel';
import { KnowledgeManager } from '../../../../src/features/voice-agents/components/KnowledgeManager';
import { OnboardingTour } from '../../../../src/features/voice-agents/components/OnboardingTour';
import {
  usePublishVoiceAgent,
  useRenameVoiceAgent,
  useVoiceAgent,
  useVoiceAgentVersions,
} from '../../../../src/features/voice-agents/hooks';
import type {
  AnalyticsPeriod,
  KnowledgeBaseItem,
} from '../../../../src/features/voice-agents/types';
import { colors } from '../../../../src/theme/colors';
import { tokens } from '../../../../src/theme/tokens';

type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';

const STATUS_BADGE_COLORS: Record<string, BadgeColor> = {
  draft: 'amber',
  published: 'blue',
  archived: 'neutral',
};

export default function VoiceAgentDetailScreen() {
  const { t } = useTranslation();
  const { id, fromOnboarding } = useLocalSearchParams<{
    id: string;
    fromOnboarding?: string;
  }>();

  const TABS = [
    { key: 'overview', label: t('voiceAgents.detail.tabOverview') },
    { key: 'config', label: t('voiceAgents.detail.tabConfig') },
    { key: 'versions', label: t('voiceAgents.detail.tabVersions') },
    { key: 'knowledge', label: t('voiceAgents.detail.tabKnowledge') },
    { key: 'experiments', label: t('voiceAgents.detail.tabExperiments') },
    { key: 'analytics', label: t('voiceAgents.detail.tabAnalytics') },
    { key: 'calls', label: t('voiceAgents.detail.tabCalls') },
  ];

  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? '';
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>('30d');
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeBaseItem[]>([]);

  const { data: agent, isLoading } = useVoiceAgent(id ?? '', orgId);
  const { data: versions = [] } = useVoiceAgentVersions(id ?? '', orgId);
  const republish = usePublishVoiceAgent();
  const rename = useRenameVoiceAgent();

  useEffect(() => {
    if (agent?.knowledgeItems) {
      setKnowledgeItems(agent.knowledgeItems);
    }
  }, [agent?.knowledgeItems]);

  if (isLoading) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.detail.loadingA11y')}
      >
        <ActivityIndicator color={colors.primary.main} size="large" />
      </View>
    );
  }

  if (!agent) {
    return (
      <View style={styles.center}>
        <Body style={{ color: colors.text.secondary }}>{t('voiceAgents.detail.notFound')}</Body>
      </View>
    );
  }

  function renderTabContent() {
    if (!agent) return null;

    switch (activeTab) {
      case 'overview':
        return (
          <ScrollView contentContainerStyle={styles.tabPad}>
            <VStack spacing="lg">
              {agent.description ? (
                <VStack spacing="xs">
                  <Heading4 style={styles.sectionTitle}>
                    {t('voiceAgents.detail.descriptionTitle')}
                  </Heading4>
                  <Body style={styles.bodyText}>{agent.description}</Body>
                </VStack>
              ) : null}

              <VStack spacing="xs">
                <Heading4 style={styles.sectionTitle}>
                  {t('voiceAgents.detail.detailsTitle')}
                </Heading4>
                <InfoRow label={t('voiceAgents.detail.useCase')} value={agent.useCase} />
                <InfoRow label={t('voiceAgents.detail.language')} value={agent.language} />
                <InfoRow label={t('voiceAgents.detail.voiceId')} value={agent.voiceId} />
                <InfoRow label={t('voiceAgents.detail.model')} value={agent.modelId} />
                {agent.publishedAt ? (
                  <InfoRow
                    label={t('voiceAgents.detail.published')}
                    value={new Date(agent.publishedAt).toLocaleString()}
                  />
                ) : null}
              </VStack>

              <View
                style={styles.placeholder}
                accessible
                accessibilityLabel={t('voiceAgents.detail.analyticsSoonA11y')}
              >
                <Caption style={{ color: colors.text.tertiary }}>
                  {t('voiceAgents.detail.analyticsSoon')}
                </Caption>
              </View>
            </VStack>
          </ScrollView>
        );

      case 'config':
        return (
          <ScrollView
            contentInsetAdjustmentBehavior="automatic"
            contentContainerStyle={styles.tabPad}
          >
            <FadeIn duration={260} translateY={6}>
              <HStack justify="between" align="center" wrap style={styles.configHeader}>
                <VStack spacing="xs" style={styles.configHeaderCopy}>
                  <Heading4 style={styles.configTitle}>
                    {t('voiceAgents.detail.configTitle')}
                  </Heading4>
                  <Caption style={styles.configDescription}>
                    {t('voiceAgents.detail.configDescription')}
                  </Caption>
                </VStack>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => router.push(`/voice-agents/${agent.id}/edit`)}
                  accessibilityLabel={t('voiceAgents.detail.editA11y')}
                >
                  <HStack spacing="xs" align="center">
                    <Pencil size={14} color={colors.primary.neon} strokeWidth={1.8} />
                    <Caption style={styles.editLabel}>{t('voiceAgents.detail.editConfig')}</Caption>
                  </HStack>
                </Button>
              </HStack>
            </FadeIn>

            <View style={styles.configGrid}>
              <FadeIn
                delay={40}
                duration={320}
                translateY={8}
                scaleFrom={0.985}
                style={[styles.configCardMotion, styles.scriptCardMotion]}
              >
                <ConfigSection
                  title={t('voiceAgents.detail.scriptTitle')}
                  icon={
                    <MessageSquareText size={18} color={colors.primary.neon} strokeWidth={1.8} />
                  }
                >
                  <InfoRow
                    label={t('voiceAgents.detail.opening')}
                    value={agent.scriptTemplate?.opening ?? '—'}
                  />
                  <InfoRow
                    label={t('voiceAgents.detail.cta')}
                    value={agent.scriptTemplate?.cta ?? '—'}
                  />
                  <InfoRow
                    label={t('voiceAgents.detail.closing')}
                    value={agent.scriptTemplate?.closing ?? '—'}
                  />
                </ConfigSection>
              </FadeIn>

              <FadeIn
                delay={90}
                duration={320}
                translateY={8}
                scaleFrom={0.985}
                style={styles.configCardMotion}
              >
                <ConfigSection
                  title={t('voiceAgents.detail.disclosureTitle')}
                  icon={
                    <ShieldCheck size={18} color={colors.status.success.main} strokeWidth={1.8} />
                  }
                >
                  <InfoRow
                    label={t('voiceAgents.detail.alwaysDisclose')}
                    value={
                      agent.disclosurePolicy?.alwaysDisclose
                        ? t('voiceAgents.detail.yes')
                        : t('voiceAgents.detail.no')
                    }
                  />
                  <InfoRow
                    label={t('voiceAgents.detail.recording')}
                    value={
                      agent.disclosurePolicy?.recordingAnnouncement
                        ? t('voiceAgents.detail.yes')
                        : t('voiceAgents.detail.no')
                    }
                  />
                </ConfigSection>
              </FadeIn>

              <FadeIn
                delay={140}
                duration={320}
                translateY={8}
                scaleFrom={0.985}
                style={styles.configCardMotion}
              >
                <ConfigSection
                  title={t('voiceAgents.detail.retryPolicyTitle')}
                  icon={
                    <RefreshCcw size={18} color={colors.status.warning.main} strokeWidth={1.8} />
                  }
                >
                  <InfoRow
                    label={t('voiceAgents.detail.maxAttempts')}
                    value={String(agent.retryPolicy?.maxAttempts ?? '—')}
                  />
                </ConfigSection>
              </FadeIn>
            </View>
          </ScrollView>
        );

      case 'versions':
        return (
          <ScrollView contentContainerStyle={styles.tabPad}>
            {versions.length === 0 ? (
              <Caption style={{ color: colors.text.tertiary }}>
                {t('voiceAgents.detail.noVersions')}
              </Caption>
            ) : (
              <VStack spacing="sm">
                {versions.map((v) => (
                  <HStack
                    key={v.id}
                    spacing="sm"
                    style={styles.versionRow}
                    accessible
                    accessibilityLabel={`${t('voiceAgents.detail.version')} ${v.version}, ${t('voiceAgents.detail.publishedLower')} ${new Date(v.publishedAt).toLocaleDateString()}`}
                  >
                    <Badge
                      color="cyan"
                      accessibilityLabel={`${t('voiceAgents.detail.version')} ${v.version}`}
                    >
                      v{v.version}
                    </Badge>
                    <Caption style={{ color: colors.text.tertiary }}>
                      {new Date(v.publishedAt).toLocaleString()}
                    </Caption>
                  </HStack>
                ))}
              </VStack>
            )}
          </ScrollView>
        );

      case 'knowledge':
        return (
          <ScrollView contentContainerStyle={styles.tabPad} keyboardShouldPersistTaps="handled">
            <KnowledgeManager
              agentId={agent.id}
              organizationId={orgId}
              items={knowledgeItems}
              onItemAdded={(item) => setKnowledgeItems((prev) => [...prev, item])}
              onItemRemoved={(itemId) =>
                setKnowledgeItems((prev) => prev.filter((i) => i.id !== itemId))
              }
            />
          </ScrollView>
        );

      case 'experiments':
        return <ExperimentsPanel agentId={agent.id} organizationId={orgId} />;

      case 'analytics':
        return (
          <ScrollView contentContainerStyle={styles.tabPad} keyboardShouldPersistTaps="handled">
            <AgentAnalyticsPanel
              agentId={agent.id}
              organizationId={orgId}
              period={analyticsPeriod}
              onPeriodChange={setAnalyticsPeriod}
            />
          </ScrollView>
        );

      case 'calls':
        return (
          <View
            style={[styles.tabPad, styles.center]}
            accessible
            accessibilityLabel={t('voiceAgents.detail.callsSoonA11y')}
          >
            <Caption style={{ color: colors.text.tertiary }}>
              {t('voiceAgents.detail.callsSoon')}
            </Caption>
          </View>
        );

      default:
        return null;
    }
  }

  return (
    <VStack style={styles.root}>
      <HStack style={styles.header} spacing="md" wrap>
        <BackButton
          accessibilityLabel={t('common.actions.goBack')}
          tooltip={t('common.actions.goBack')}
          fallbackHref="/voice-agents"
        />
        <VStack style={{ flex: 1 }} spacing="xs">
          {/* FLOSEAI-656 — renaming lives here, not in the wizard: the wizard is
              the configuration publishing froze, while the name is the one
              thing a published agent must still be able to change. */}
          <AgentNameField
            name={agent.name}
            isSaving={rename.isPending}
            editable={agent.status !== 'archived'}
            onSave={(name) => rename.mutate({ id: agent.id, organizationId: orgId, name })}
          />
          <HStack spacing="sm" style={{ alignItems: 'center' }}>
            <Badge
              color={STATUS_BADGE_COLORS[agent.status]}
              accessibilityLabel={`${t('voiceAgents.detail.statusLabel')}: ${agent.status}`}
            >
              {agent.status}
            </Badge>
            <Caption style={styles.version}>v{agent.version}</Caption>
          </HStack>
        </VStack>

        <HStack spacing="sm" wrap style={styles.headerActions}>
          <Button
            variant="ghost"
            size="sm"
            onPress={() => router.push(`/voice-agents/${agent.id}/preview`)}
            accessibilityLabel={t('voiceAgents.detail.previewA11y')}
            accessibilityRole="button"
          >
            {t('voiceAgents.detail.preview')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onPress={() => router.push(`/voice-agents/${agent.id}/edit`)}
            accessibilityLabel={t('voiceAgents.detail.editA11y')}
          >
            <HStack spacing="xs" align="center">
              <Pencil size={14} color={colors.primary.neon} strokeWidth={1.8} />
              <Caption style={styles.editLabel}>{t('voiceAgents.detail.edit')}</Caption>
            </HStack>
          </Button>
          {agent.status === 'published' && (
            <Button
              variant="primary"
              size="sm"
              loading={republish.isPending}
              onPress={() => republish.mutate({ id: agent.id, organizationId: orgId })}
              accessibilityLabel={t('voiceAgents.detail.republishA11y')}
              accessibilityRole="button"
            >
              {t('voiceAgents.detail.republish')}
            </Button>
          )}
        </HStack>
      </HStack>

      <View style={styles.tabsShell}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <Tabs
            tabs={TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            variant="underline"
            testID="agent-detail-tabs"
          />
        </ScrollView>
      </View>

      <View style={styles.tabContent}>
        <FadeIn
          key={activeTab}
          duration={240}
          translateY={6}
          scaleFrom={0.995}
          style={styles.tabTransition}
        >
          {renderTabContent()}
        </FadeIn>
      </View>

      {fromOnboarding === 'true' ? <OnboardingTour /> : null}
    </VStack>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <HStack
      spacing="sm"
      style={styles.infoRow}
      accessible
      accessibilityLabel={`${label}: ${value}`}
    >
      <Caption style={styles.infoLabel}>{label}</Caption>
      <Body style={styles.infoValue}>{value}</Body>
    </HStack>
  );
}

function ConfigSection({
  title,
  icon,
  children,
  style,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <View style={[styles.configCard, style]} accessible accessibilityLabel={title}>
      <HStack spacing="sm" align="center" style={styles.configCardHeader}>
        <View style={styles.configIcon}>{icon}</View>
        <Heading4 style={styles.configCardTitle}>{title}</Heading4>
      </HStack>
      <VStack spacing="none">{children}</VStack>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    padding: 20,
    paddingBottom: 16,
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
    backgroundColor: colors.background.secondary,
    rowGap: tokens.spacing.sm,
  },
  headerActions: {
    flexShrink: 0,
  },
  name: {
    color: colors.text.primary,
    fontSize: 22,
  },
  version: {
    color: colors.text.tertiary,
    fontSize: 13,
  },
  tabContent: {
    flex: 1,
  },
  tabTransition: {
    flex: 1,
  },
  tabsShell: {
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
  },
  tabPad: {
    padding: 20,
    gap: 12,
    paddingBottom: 40,
  },
  sectionTitle: {
    color: colors.text.primary,
    marginTop: 16,
    marginBottom: 4,
  },
  configHeader: {
    width: '100%',
    rowGap: tokens.spacing.sm,
    marginBottom: tokens.spacing.sm,
  },
  configHeaderCopy: {
    flex: 1,
    minWidth: 220,
  },
  configTitle: {
    color: colors.text.primary,
  },
  configDescription: {
    color: colors.text.tertiary,
    lineHeight: 18,
  },
  editLabel: {
    color: colors.primary.neon,
    fontWeight: tokens.fontWeight.semibold,
  },
  configGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.md,
  },
  configCard: {
    width: '100%',
    height: '100%',
    padding: tokens.spacing.lg,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
  },
  configCardMotion: {
    flex: 1,
    flexBasis: 280,
    minWidth: 240,
  },
  scriptCardMotion: {
    flexBasis: 420,
  },
  configCardHeader: {
    paddingBottom: tokens.spacing.md,
    marginBottom: tokens.spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.subtle,
  },
  configIcon: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.elevated,
  },
  configCardTitle: {
    color: colors.text.primary,
    margin: 0,
  },
  bodyText: {
    color: colors.text.secondary,
    lineHeight: 22,
  },
  placeholder: {
    backgroundColor: colors.background.secondary,
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    marginTop: 8,
  },
  versionRow: {
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  infoRow: {
    alignItems: 'flex-start',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.background.elevated,
  },
  infoLabel: {
    color: colors.text.tertiary,
    width: 130,
    fontSize: 13,
  },
  infoValue: {
    color: colors.text.primary,
    flex: 1,
    fontSize: 14,
  },
});
